import React, { useState, useEffect } from 'react';
import './CallOffsPage.css';

const CallOffsPage = ({ onStockUpdate }) => {
  const [callOffs, setCallOffs] = useState([]);
  const [filteredCallOffs, setFilteredCallOffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('scheduled');
  const [clientFilter, setClientFilter] = useState('');
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchCallOffs();
    fetchClients();
  }, []);

  useEffect(() => {
    filterCallOffs();
  }, [callOffs, statusFilter, clientFilter]);

  const fetchCallOffs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/call-offs`);
      if (!response.ok) {
        throw new Error('Failed to fetch call-offs');
      }
      const data = await response.json();
      setCallOffs(data);
    } catch (error) {
      console.error('Error fetching call-offs:', error);
      setError('Failed to load call-offs');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/clients`);
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const filterCallOffs = () => {
    let filtered = callOffs;

    if (statusFilter) {
      filtered = filtered.filter(callOff => callOff.status === statusFilter);
    }

    if (clientFilter) {
      filtered = filtered.filter(callOff => 
        callOff.stockItem.client?.id === clientFilter
      );
    }

    setFilteredCallOffs(filtered);
  };

  const handleConvertToBooking = async (callOffId) => {
    if (!window.confirm('Convert this call-off to a booking now? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/call-offs/${callOffId}/convert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to convert call-off');
      }

      const result = await response.json();
      alert(result.message);
      fetchCallOffs(); // Refresh the list
      
      // Notify parent component to refresh stock data
      console.log('Call-off converted, refreshing stock data...');
      if (onStockUpdate) {
        onStockUpdate();
        console.log('Stock update function called');
      } else {
        console.log('No onStockUpdate function provided');
      }
    } catch (error) {
      console.error('Error converting call-off:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleCancelCallOff = async (callOffId) => {
    if (!window.confirm('Cancel this call-off? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/call-offs/${callOffId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel call-off');
      }

      alert('Call-off cancelled successfully');
      fetchCallOffs(); // Refresh the list
    } catch (error) {
      console.error('Error cancelling call-off:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      scheduled: 'status-scheduled',
      converted: 'status-converted',
      cancelled: 'status-cancelled'
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ''}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getDaysUntilBooking = (bookingDate) => {
    const today = new Date();
    const booking = new Date(bookingDate);
    const diffTime = booking - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  if (loading) {
    return <div className="loading">Loading call-offs...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="call-offs-page">
      <div className="page-header">
        <h1>Call-Off Orders</h1>
        <p>Manage advance orders scheduled for future booking conversion</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="converted">Converted</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="clientFilter">Client:</label>
          <select
            id="clientFilter"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <option value="">All Clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        <button onClick={fetchCallOffs} className="refresh-button">
          Refresh
        </button>
      </div>

      <div className="call-offs-summary">
        <div className="summary-card">
          <h3>Total Call-Offs</h3>
          <span className="summary-number">{filteredCallOffs.length}</span>
        </div>
        <div className="summary-card">
          <h3>Scheduled</h3>
          <span className="summary-number">
            {filteredCallOffs.filter(c => c.status === 'scheduled').length}
          </span>
        </div>
        <div className="summary-card">
          <h3>Due Soon</h3>
          <span className="summary-number">
            {filteredCallOffs.filter(c => {
              const days = getDaysUntilBooking(c.bookingDate);
              return c.status === 'scheduled' && (days === 'Today' || days === 'Tomorrow' || days === 'Overdue');
            }).length}
          </span>
        </div>
      </div>

      <div className="call-offs-table-container">
        <table className="call-offs-table">
          <thead>
            <tr>
              <th>Work Ticket</th>
              <th>Product Code</th>
              <th>Client</th>
              <th>Quantity</th>
              <th>Booking Date</th>
              <th>Days Until</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCallOffs.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No call-offs found matching the current filters
                </td>
              </tr>
            ) : (
              filteredCallOffs.map(callOff => (
                <tr key={callOff.id} className={`call-off-row status-${callOff.status}`}>
                  <td className="work-ticket">{callOff.orderNumber}</td>
                  <td className="product-code">
                    <div>
                      <strong>{callOff.stockItem.productCode}</strong>
                      <small>{callOff.stockItem.variantDesc}</small>
                    </div>
                  </td>
                  <td>{callOff.stockItem.client?.name || 'General Stock'}</td>
                  <td className="quantity">{callOff.qty}</td>
                  <td>{formatDate(callOff.bookingDate)}</td>
                  <td className={`days-until ${getDaysUntilBooking(callOff.bookingDate) === 'Overdue' ? 'overdue' : ''}`}>
                    {getDaysUntilBooking(callOff.bookingDate)}
                  </td>
                  <td>{getStatusBadge(callOff.status)}</td>
                  <td>{formatDateTime(callOff.createdAt)}</td>
                  <td className="actions">
                    {callOff.status === 'scheduled' && (
                      <>
                        <button
                          onClick={() => handleConvertToBooking(callOff.id)}
                          className="convert-button"
                          title="Convert to booking now"
                        >
                          Convert
                        </button>
                        <button
                          onClick={() => handleCancelCallOff(callOff.id)}
                          className="cancel-button"
                          title="Cancel call-off"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {callOff.status === 'converted' && (
                      <span className="converted-info">
                        Converted {callOff.convertedBy === 'manual' ? 'manually' : 'automatically'}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default CallOffsPage;
